/**
 * Funnel Tracking Service
 * 
 * Tracks user journey from widget open to conversation completion.
 * This data is used for conversion funnel analytics.
 * 
 * Flow:
 * 1. Widget opened -> createSession() -> stores sessionId in localStorage
 * 2. First message sent -> markConversationStarted()
 * 3. [DONE] flag received -> markConversationFinished()
 * 4. User signs in on platform -> (handled by platform, links session to userId)
 */

const FUNNEL_SESSION_KEY = 'imaven_funnel_session';
const FUNNEL_API_BASE = process.env.REACT_APP_API_BASE_URL || '';

/**
 * Get device/browser info for tracking
 */
const getDeviceInfo = () => {
    return {
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform
    };
};

/**
 * Get source tracking info from URL params
 */
const getSourceInfo = () => {
    const params = new URLSearchParams(window.location.search);
    return {
        referrer: document.referrer || undefined,
        utmSource: params.get('utm_source') || undefined,
        utmMedium: params.get('utm_medium') || undefined,
        utmCampaign: params.get('utm_campaign') || undefined
    };
};

/**
 * Get stored session ID from localStorage
 */
export const getSessionId = () => {
    try {
        const session = localStorage.getItem(FUNNEL_SESSION_KEY);
        if (session) {
            const parsed = JSON.parse(session);
            return parsed.sessionId;
        }
    } catch (e) {
        console.warn('[Funnel] Error reading session:', e);
    }
    return null;
};

/**
 * Create a new funnel session when widget opens
 * @param {string} expertId - The expert's user ID
 */
export const createFunnelSession = async (expertId) => {
    try {
        // Check if we already have a valid session
        const existingSessionId = getSessionId();
        if (existingSessionId) {
            console.log('[Funnel] Using existing session:', existingSessionId);
            return existingSessionId;
        }
        
        const response = await fetch(`${FUNNEL_API_BASE}/funnel/session`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                expertId,
                source: getSourceInfo(),
                deviceInfo: getDeviceInfo()
            })
        });
        
        if (response.ok) {
            const data = await response.json();
            
            // Store session in localStorage
            localStorage.setItem(FUNNEL_SESSION_KEY, JSON.stringify({
                sessionId: data.sessionId,
                expertId,
                createdAt: new Date().toISOString()
            }));
            
            console.log('[Funnel] Session created:', data.sessionId);
            return data.sessionId;
        } else {
            console.warn('[Funnel] Failed to create session:', response.status);
            return null;
        }
    } catch (error) {
        console.error('[Funnel] Error creating session:', error);
        return null;
    }
};

/**
 * Mark conversation as started (first message sent)
 * @param {string} conversationId - The conversation ID
 */
export const markConversationStarted = async (conversationId) => {
    try {
        const sessionId = getSessionId();
        if (!sessionId) {
            console.warn('[Funnel] No session found for marking conversation started');
            return false;
        }
        
        // Reset the drop signal flag so a new drop can be detected for this conversation
        dropSignalSent = false;
        
        const response = await fetch(`${FUNNEL_API_BASE}/funnel/session/${sessionId}/started`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ conversationId })
        });
        
        if (response.ok) {
            console.log('[Funnel] Conversation started marked:', sessionId);
            return true;
        } else {
            console.warn('[Funnel] Failed to mark conversation started:', response.status);
            return false;
        }
    } catch (error) {
        console.error('[Funnel] Error marking conversation started:', error);
        return false;
    }
};

/**
 * Mark conversation as finished ([DONE] flag received)
 */
export const markConversationFinished = async () => {
    try {
        const sessionId = getSessionId();
        if (!sessionId) {
            console.warn('[Funnel] No session found for marking conversation finished');
            return false;
        }
        
        const response = await fetch(`${FUNNEL_API_BASE}/funnel/session/${sessionId}/finished`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json'
            }
        });
        
        if (response.ok) {
            console.log('[Funnel] Conversation finished marked:', sessionId);
            return true;
        } else {
            console.warn('[Funnel] Failed to mark conversation finished:', response.status);
            return false;
        }
    } catch (error) {
        console.error('[Funnel] Error marking conversation finished:', error);
        return false;
    }
};

// Client-side guard: only fire one drop signal per page lifecycle
let dropSignalSent = false;

/**
 * Mark session as dropped when user closes the widget/browser.
 * Uses navigator.sendBeacon for reliable delivery during page unload.
 * Only fires once per page load to prevent duplicate emails.
 * @param {number} messageCount - Number of messages in the conversation
 * @param {string} conversationLanguage - Detected language code (e.g., 'es', 'pt')
 */
export const markSessionDropped = (messageCount = 0, conversationLanguage = 'en') => {
    if (dropSignalSent) return;
    
    try {
        const sessionId = getSessionId();
        if (!sessionId) return;
        
        dropSignalSent = true;
        
        const url = `${FUNNEL_API_BASE}/funnel/session/${sessionId}/dropped`;
        const payload = JSON.stringify({ messageCount, conversationLanguage });
        
        if (navigator.sendBeacon) {
            const blob = new Blob([payload], { type: 'application/json' });
            navigator.sendBeacon(url, blob);
        } else {
            fetch(url, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: payload,
                keepalive: true
            }).catch(() => {});
        }
    } catch (e) {
        // Fail silently during unload
    }
};

/**
 * Store user contact info in the funnel session
 * @param {string} userEmail
 * @param {string} userPhone
 * @param {string} userName
 */
export const storeContactInfo = async (userEmail, userPhone, userName) => {
    try {
        const sessionId = getSessionId();
        if (!sessionId) return false;
        
        const response = await fetch(`${FUNNEL_API_BASE}/funnel/session/${sessionId}/contact`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userEmail, userPhone, userName })
        });
        
        return response.ok;
    } catch (error) {
        console.warn('[Funnel] Error storing contact info:', error);
        return false;
    }
};

/**
 * Clear session (for testing or when user starts fresh)
 */
export const clearSession = () => {
    localStorage.removeItem(FUNNEL_SESSION_KEY);
    console.log('[Funnel] Session cleared');
};

export default {
    createFunnelSession,
    getSessionId,
    markConversationStarted,
    markConversationFinished,
    markSessionDropped,
    storeContactInfo,
    clearSession
};

